import { ElementTypes } from './element';

// Space Configs that are shared by all
export interface ISpace<T> {
  // Identifier
  id: string;
  // Field Id,
  fieldId?: string;
  // Left & Right Position
  posX: number;
  // Up & Down Position
  posY: number;
  // Element Width
  width: number;
  // Element Height
  height: number;
  // Rotation in degrees
  rotation: number;
  // Order
  order: number;
  // If true, element detects for collisions, and dynamic depending on content.
  isAvoidCollision: boolean;
  // Type of element. Static text, dynamic etc.
  type: T;
  // Other visual space configurations
  config: IConfig<T>;
  // The configurable data that saves into the background. More important for text and field record references. For example,
  // the static text, and which columns we show in the record ref & sorting.
}

export interface IShadow {
  // Identifier
  id: string;
  // Left & Right Position
  posX: number;
  // Up & Down Position
  posY: number;
  // Element Width
  width: number;
  // Element Height
  height: number;
}

type IStaticTextConfig = IBackgroundConfig & IFontConfigs & IContentConfig;
type IStaticAttachmentConfig = IBackgroundConfig & IImageContentConfig;
type IFieldTextConfig = IBackgroundConfig & IFontConfigs;
type IFieldLongTextConfig = IBackgroundConfig & IFontConfigs;
type IFieldNumberConfig = IBackgroundConfig & IFontConfigs;
type IFieldSelectConfig = IBackgroundConfig;
type IFieldMultiSelectConfig = IBackgroundConfig;
type IFieldAttachmentConfig = IBackgroundConfig & IImageConfig;
type IFieldRecordRefConfig = IBackgroundConfig & ITableConfig & IAttachmentSizeConfig;
type IFieldLookupConfig = IBackgroundConfig & IFontConfigs & IImageConfig & ITableConfig;

// By default it will only be IBackgroundConfig
export type IConfig<T> = T extends ElementTypes.StaticText
  ? IStaticTextConfig
  : T extends ElementTypes.StaticAttachment
  ? IStaticAttachmentConfig
  : T extends ElementTypes.FieldText
  ? IFieldTextConfig
  : T extends ElementTypes.FieldLongText
  ? IFieldLongTextConfig
  : T extends ElementTypes.FieldNumber
  ? IFieldNumberConfig
  : T extends ElementTypes.FieldSelect
  ? IFieldSelectConfig
  : T extends ElementTypes.FieldMultiSelect
  ? IFieldMultiSelectConfig
  : T extends ElementTypes.FieldMultiAttachment
  ? IFieldAttachmentConfig
  : T extends ElementTypes.FieldRecordRef
  ? IFieldRecordRefConfig
  : T extends ElementTypes.FieldLookup
  ? IFieldLookupConfig
  : IBackgroundConfig;

// All elements have this
export interface IBackgroundConfig {
  background: BackgroundType;
  backgroundColor?: string;
  padding: number;
  borderRadius: number;
}

// For static text
export interface IContentConfig {
  content: string | null;
}
export interface IImageContentConfig {
  imageContent: IImageContent;
}

export interface IImageContent {
  name: string;
  url: string;
  largeThumbUrl: string;
}

// Most likely for elements that are text based
export interface IFontConfigs {
  font: string;
  fontSize: number;
  fontWeight: string;
  lineHeight: string;
  horizontalAlign: HorizontalAlignment;
  verticalAlign: VerticalAlignment;
  textColor: string;
}

// For Dynamic Image
export interface IImageConfig {
  imageDisplay: ImageDisplayType;
  imageSizing: ImageSizingType;
  imageSize: number;
  imagePadding: number;
}

// Custom sizes for attachment images in record reference table
export interface IAttachmentSizeConfig {
  attachmentHeight?: { [id: string]: number | undefined };
}

// Record References
export interface IRecordSort {
  id: string;
  direction: string;
}
// *ONLY* columns that are record refs should have another tableColumn nested inside
export interface IRefTableColumn {
  id: string;
  displayName?: string;
  uid: string;
  borderStyle: BorderType;
  listStyle: ListStyleType;
  showHeadings: boolean;
  showRefAsText: boolean;
  headingStyle: IFontConfigs;
  rowStyle: IFontConfigs;
  tableMode: TableModeType;
  tableSorting: IRecordSort[];
  tableColumns?: IRefTableColumn[];
}

export interface ITableConfig {
  borderStyle: BorderType;
  headingStyle: IFontConfigs;
  listStyle: ListStyleType;
  showHeadings: boolean;
  showRefAsText: boolean;
  tableMode: TableModeType;
  rowStyle: IFontConfigs;

  tableSorting: IRecordSort[];
  tableColumns: IRefTableColumn[];
}

export enum TableModeType {
  Table = 'Table',
  List = 'List',
}
export enum ListStyleType {
  Bullets = 'Bullets',
  Numbers = 'Numbers',
  None = 'None',
}
export enum BorderType {
  All = 'All',
  Inner = 'Inner',
  Vertical = 'Vertical',
  Horizontal = 'Horizontal',
  Heading = 'Heading',
  None = 'None',
}
export enum ImageDisplayType {
  First = 'First',
  All = 'All',
}
export enum ImageSizingType {
  Fit = 'Fit',
  Fill = 'Fill',
  Stretch = 'Stretch',
}
export enum HorizontalAlignment {
  Left = 'flex-start',
  Center = 'center',
  Right = 'flex-end',
}
export enum VerticalAlignment {
  Top = 'flex-start',
  Center = 'center',
  Bottom = 'flex-end',
}
export enum BackgroundType {
  Transparent = 'Transparent',
  Filled = 'Filled',
}
export enum OrderChangeType {
  AllBack = 'AllBack',
  AllForward = 'AllForward',
  OneBack = 'OneBack',
  OneForward = 'OneForward',
}
